import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  AccountTree,
  Check,
  Business,
  Warning,
  Logout,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../hooks/useTenant';
import { Tenant } from '../types/tenant';

interface TenantSelectorProps {
  /** Posição do menu */
  position?: 'top' | 'bottom';
  /** Se deve mostrar o seletor */
  show?: boolean;
  /** Callback chamado quando um tenant é selecionado */
  onTenantSelect?: (tenant: Tenant) => void;
}

export const TenantSelector: React.FC<TenantSelectorProps> = ({
  position = 'bottom',
  show = true,
  onTenantSelect,
}) => {
  const { currentTenant, availableTenants, selectTenant, isTenantSelected } = useTenant();
  const { logout } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantSelect = (tenant: Tenant) => {
    selectTenant(tenant.name);
    onTenantSelect?.(tenant);
    handleClose();
  };

  if (!show) {
    return null;
  }

  // Se não há tenants disponíveis, mostrar mensagem de erro com opção de logout
  if (availableTenants.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
        <Tooltip title="Você não tem acesso a nenhum tenant">
          <Button
            variant="outlined"
            color="error"
            startIcon={<Warning />}
            disabled
            fullWidth
            sx={{
              borderColor: 'rgba(255, 82, 82, 0.5)',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'not-allowed',
              fontSize: '11px',
              padding: '6px 12px',
              '&.Mui-disabled': {
                borderColor: 'rgba(255, 82, 82, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            Sem Acesso
          </Button>
        </Tooltip>
        <Tooltip title="Sair da aplicação">
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Logout />}
            fullWidth
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '11px',
              padding: '6px 12px',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Sair
          </Button>
        </Tooltip>
      </Box>
    );
  }

  // Se não há tenant selecionado, mostrar botão para selecionar
  if (!isTenantSelected) {
    return (
      <Tooltip title="Selecionar Tenant">
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Business />}
          onClick={handleClick}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'white',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Selecionar Tenant
        </Button>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Tooltip title="Alterar Tenant">
        <Button
          onClick={handleClick}
          sx={{
            color: 'white',
            textTransform: 'none',
            minWidth: 'auto',
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '14px',
              backgroundColor: currentTenant?.color || '#666',
            }}
          >
            {currentTenant?.name.charAt(0).toUpperCase()}
          </Avatar>
        </Button>
      </Tooltip>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'white',
          fontSize: '10px',
          fontWeight: 500,
          mt: 0.5,
          textAlign: 'center',
        }}
      >
        {currentTenant?.name}
      </Typography>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: position === 'top' ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: position === 'top' ? 'bottom' : 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxHeight: 400,
            backgroundColor: '#1e1e1e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            '& .MuiMenuItem-root': {
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              },
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
            Selecionar Tenant
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Escolha o tenant para gerenciar
          </Typography>
        </Box>

        {/* Lista de Tenants */}
        {availableTenants.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Warning sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Nenhum tenant disponível
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Entre em contato com o administrador para obter acesso
            </Typography>
          </Box>
        ) : (
          availableTenants.map((tenant) => (
            <MenuItem
              key={tenant.name}
              onClick={() => handleTenantSelect(tenant)}
              selected={currentTenant?.name === tenant.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '14px',
                    backgroundColor: tenant.color || '#666',
                  }}
                >
                  {tenant.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemIcon>

              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {tenant.name}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {tenant.description}
                  </Typography>
                }
              />

              {currentTenant?.name === tenant.name && (
                <Check sx={{ color: 'primary.main', fontSize: 20 }} />
              )}
            </MenuItem>
          ))
        )}

        <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Footer */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Chip
            icon={<AccountTree />}
            label={`${availableTenants.length} tenants disponíveis`}
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '11px',
            }}
          />
        </Box>
      </Menu>
    </Box>
  );
};
